<template>
  <v-container class="big-cat-contest">
    <!-- Hero/Announcement Section -->
    <section class="hero-section">
      <p class="season-notice">Stay tuned — next season dates coming soon</p>
      <h1 class="hero-title">Second Annual Big Cat Contest</h1>
      <p class="hero-subtitle">
        The Black Hills Mountain Lion Foundation, in partnership with the South Dakota Predator Hunters Association,
        is proud to announce the Second Annual Big Cat Contest during the next Black Hills Fire Protection District
        mountain lion season.
      </p>
      <div class="partnership-info">
        <p><strong>Presented in Partnership By:</strong></p>
        <p>The Black Hills Mountain Lion Foundation</p>
        <p>and</p>
        <p>The Predator Hunter Association</p>
      </div>
      <p class="hero-subtitle">The registration link will be posted here when it is ready.</p>
    </section>

    <v-divider class="my-6"></v-divider>

    <!-- Contest Overview Section -->
    <section class="contest-overview">
      <v-card class="overview-card">
        <v-card-title class="section-title">CONTEST OVERVIEW</v-card-title>
        <v-card-text>
          <p class="overview-text">
            This is a big cat contest. The heaviest cat will win – one in the <strong>BIG MALE</strong> category 
            and one in the <strong>BIG FEMALE</strong> category. This contest will run for the entire season of 
            the next Black Hills Fire District season. All season dates, details, rules, regulations, and quota 
            information should be referenced through the 
            <a href="https://gfp.sd.gov/" target="_blank" rel="noopener noreferrer" class="external-link">
              South Dakota Game, Fish and Parks website
            </a>.
          </p>
          <div class="rules-highlight">
            <h3>🏹 Contest Rules</h3>
            <ul class="rules-list">
              <li>Mountain Lions MUST BE obtained by <strong>foot hunting or calling ONLY</strong></li>
              <li><strong>NO hound-hunted cats</strong> are allowed in this contest</li>
            </ul>
          </div>
          <div class="entry-fee-info">
            <p><strong>Entry Fee:</strong> $25</p>
            <p><strong>Payback:</strong> 80 percent payback from the entry fee for awards</p>
          </div>
        </v-card-text>
      </v-card>
    </section>

    <v-divider class="my-6"></v-divider>

    <!-- Registration Information Section -->
    <section class="registration-section">
      <v-card class="registration-card">
        <v-card-title class="section-title">REGISTRATION INFORMATION</v-card-title>
        <v-card-text>
          <p class="registration-note">
            Registration and payment will be required online. The registration link will be posted here when it is ready.
          </p>
          <ul class="registration-list">
            <li>You must register <strong>in advance</strong> of your hunt</li>
            <li>You must be registered for this contest <strong>at least 5 days before</strong> you harvest your cat</li>
            <li class="important-rule">This means <strong>no shooting a cat and then registering</strong> for the contest</li>
          </ul>
        </v-card-text>
      </v-card>
    </section>

    <v-divider class="my-6"></v-divider>

    <!-- Verification Paperwork Section -->
    <section class="verification-section">
      <v-card class="verification-card">
        <v-card-title class="section-title">VERIFICATION PAPERWORK REQUIRED</v-card-title>
        <v-card-text>
          <p class="verification-text">
            After you have checked your mountain lion in with the South Dakota Game, Fish and Parks – you must 
            submit a copy of the state paperwork that the GFP employee completes when the cat is checked in.
          </p>
          <ul class="verification-list">
            <li>All information must be <strong>legible</strong></li>
            <li>Email a copy/photo of this form to <strong>Chad Kiel</strong> – President of The Predator Hunter Association</li>
            <li>Email: <a href="mailto:thepredatorhunterassociation@gmail.com" class="email-link">thepredatorhunterassociation@gmail.com</a></li>
            <li>Subject line <strong>MUST READ:</strong> <code class="subject-line">Second Annual Big Cat Contest</code></li>
          </ul>
        </v-card-text>
      </v-card>
    </section>

    <v-divider class="my-6"></v-divider>

    <!-- Awards Section -->
    <section class="awards-section">
      <h2 class="section-title">AWARDS</h2>
      <v-row justify="center" class="mb-4">
        <v-col cols="12" md="8" lg="6">
          <v-card class="award-card">
            <v-card-text>
              <div class="award-item">
                <h3>🥇 Biggest Male Mountain Lion</h3>
                <p class="award-description">Custom Champion Buckle</p>
              </div>
              <v-divider class="my-4"></v-divider>
              <div class="award-item">
                <h3>🥇 Biggest Female Mountain Lion</h3>
                <p class="award-description">Custom Champion Buckle</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Buckle Image -->
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <v-card class="buckle-card">
            <v-card-text>
              <p class="buckle-caption">📸 Photo shown: Big Male Champion Buckle</p>
              <v-img
                :src="require('@/assets/2026BigCatBuckle.jpg')"
                alt="Big Male Champion Buckle"
                class="buckle-image"
                max-width="600"
              ></v-img>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <div class="awards-timing">
        <p>
          Once the season is closed, The Predator Hunter Association
          will determine the winners. The awards presentation date will be announced.
        </p>
        <p>
          Trophy buckles and prize money will be awarded in both categories, for the heaviest male and heaviest female.
        </p>
      </div>
      <p class="awards-footer">
        This contest celebrates ethical, legal, and science-based mountain lion management while recognizing 
        exceptional harvests during the season.
      </p>
    </section>

    <v-divider class="my-6"></v-divider>

    <v-divider class="my-6"></v-divider>

    <!-- Contact Information Section -->
    <section class="contact-section">
      <v-card class="contact-card">
        <v-card-title class="section-title">CONTACT INFORMATION</v-card-title>
        <v-card-text>
          <p class="contact-text">
            For more details and information, please contact:
          </p>
          <div class="contact-details">
            <p><strong>Chad Kiel</strong></p>
            <p>President of The Predator Hunter Association</p>
            <p>
              <a href="tel:6052221873" class="phone-link">605.222.1873</a>
            </p>
            <p>
              <a href="mailto:thepredatorhunterassociation@gmail.com" class="email-link">
                thepredatorhunterassociation@gmail.com
              </a>
            </p>
          </div>
        </v-card-text>
      </v-card>
    </section>

    <v-divider class="my-6"></v-divider>

    <!-- Call to Action Section -->
    <section class="call-to-action">
      <h2 class="cta-title">📢 Ready to compete?</h2>
      <p class="cta-text">Follow. Share. Get ready.</p>
      <p class="cta-highlight">This is one you won't want to miss.</p>
    </section>
  </v-container>
</template>

<script>
export default {
  name: "BigCatContest",
};
</script>

<style scoped>
.big-cat-contest {
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 40px 20px;
  background: var(--bh-surface, #fff);
  border: 1px solid var(--bh-border, #e4e4e4);
  border-radius: 8px;
  margin-bottom: 20px;
}

.season-notice {
  display: inline-block;
  margin: 0 0 12px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(193, 122, 47, 0.12);
  color: #a36626;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.hero-title {
  font-size: 2.5em;
  font-weight: bold;
  margin-bottom: 20px;
  color: #2c3e50;
}

.hero-subtitle {
  font-size: 1.2em;
  line-height: 1.8;
  color: #555;
  max-width: 900px;
  margin: 0 auto 20px;
}

.partnership-info {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid #42b983;
}

.partnership-info p {
  margin: 8px 0;
  font-size: 1.1em;
  color: #2c3e50;
}

/* Section Titles */
.section-title {
  font-size: 2em;
  text-align: center;
  margin-bottom: 30px;
  color: #2c3e50;
  font-weight: bold;
}

/* Contest Overview */
.contest-overview {
  margin: 40px 0;
}

.overview-card {
  max-width: 900px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.overview-text {
  font-size: 1.1em;
  line-height: 1.8;
  margin-bottom: 25px;
  text-align: left;
}

.external-link {
  color: #42b983;
  text-decoration: underline;
  font-weight: 500;
}

.external-link:hover {
  color: #36926c;
}

.rules-highlight {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  margin: 25px 0;
  border-left: 4px solid #42b983;
}

.rules-highlight h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.3em;
}

.rules-list {
  list-style: none;
  padding-left: 0;
  text-align: left;
  font-size: 1.1em;
  line-height: 2;
}

.rules-list li {
  padding: 8px 0;
  padding-left: 30px;
  position: relative;
}

.rules-list li:before {
  content: "✓";
  position: absolute;
  left: 10px;
  color: #42b983;
  font-size: 1.2em;
  font-weight: bold;
}

.entry-fee-info {
  margin-top: 25px;
  padding: 20px;
  background: #e8f5e9;
  border-radius: 8px;
  text-align: center;
}

.entry-fee-info p {
  margin: 8px 0;
  font-size: 1.2em;
  color: #2c3e50;
}

.details-list {
  list-style: none;
  padding-left: 0;
  text-align: left;
  font-size: 1.1em;
  line-height: 2;
}

.details-list li {
  padding: 8px 0;
  padding-left: 30px;
  position: relative;
}

.details-list li:before {
  content: "•";
  position: absolute;
  left: 10px;
  color: #42b983;
  font-size: 1.5em;
  font-weight: bold;
}

/* Registration Section */
.registration-section {
  margin: 40px 0;
}

.registration-card {
  max-width: 900px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 5px solid #42b983;
}

.registration-note {
  font-size: 1.2em;
  margin-bottom: 20px;
  text-align: center;
  color: #2c3e50;
  font-weight: 500;
}

.registration-list {
  list-style: none;
  padding-left: 0;
  text-align: left;
  font-size: 1.1em;
  line-height: 2.2;
}

.registration-list li {
  padding: 10px 0;
  padding-left: 30px;
  position: relative;
}

.registration-list li:before {
  content: "→";
  position: absolute;
  left: 10px;
  color: #42b983;
  font-size: 1.3em;
  font-weight: bold;
}

.registration-list .important-rule {
  background: #fff3cd;
  padding: 15px;
  border-radius: 5px;
  margin-top: 15px;
  border-left: 4px solid #ff9800;
}

/* Verification Section */
.verification-section {
  margin: 40px 0;
}

.verification-card {
  max-width: 900px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 5px solid #ff9800;
}

.verification-text {
  font-size: 1.1em;
  line-height: 1.8;
  margin-bottom: 20px;
  text-align: left;
}

.verification-list {
  list-style: none;
  padding-left: 0;
  text-align: left;
  font-size: 1.1em;
  line-height: 2;
}

.verification-list li {
  padding: 10px 0;
  padding-left: 30px;
  position: relative;
}

.verification-list li:before {
  content: "📄";
  position: absolute;
  left: 5px;
  font-size: 1.2em;
}

.email-link {
  color: #42b983;
  text-decoration: none;
  font-weight: 500;
}

.email-link:hover {
  color: #36926c;
  text-decoration: underline;
}

.subject-line {
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  color: #2c3e50;
  font-weight: bold;
}

/* Awards Section */
.awards-section {
  margin: 40px 0;
  text-align: center;
}

.award-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.award-item {
  text-align: center;
}

.award-item h3 {
  font-size: 1.5em;
  margin-bottom: 10px;
  color: #2c3e50;
}

.award-description {
  font-size: 1.2em;
  color: #666;
  font-weight: 500;
}

.buckle-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}

.buckle-caption {
  font-size: 1.1em;
  margin-bottom: 20px;
  color: #666;
  font-style: italic;
}

.buckle-image {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
}

.awards-timing {
  margin-top: 30px;
  padding: 20px;
  background: #e3f2fd;
  border-radius: 8px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.awards-timing p {
  font-size: 1.1em;
  line-height: 1.8;
  color: #2c3e50;
  margin: 10px 0;
  text-align: left;
}

.awards-footer {
  margin-top: 30px;
  font-size: 1.1em;
  line-height: 1.8;
  color: #555;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

/* Contact Section */
.contact-section {
  margin: 40px 0;
}

.contact-card {
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #f9f9f9 0%, #ffffff 100%);
}

.contact-text {
  font-size: 1.2em;
  margin-bottom: 25px;
  text-align: center;
  color: #2c3e50;
}

.contact-details {
  text-align: center;
  padding: 20px;
  background: white;
  border-radius: 8px;
}

.contact-details p {
  margin: 12px 0;
  font-size: 1.1em;
  color: #2c3e50;
}

.phone-link {
  color: #42b983;
  text-decoration: none;
  font-size: 1.3em;
  font-weight: bold;
}

.phone-link:hover {
  color: #36926c;
  text-decoration: underline;
}

/* Call to Action */
.call-to-action {
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #42b983 0%, #36926c 100%);
  border-radius: 8px;
  color: white;
  margin-top: 40px;
}

.cta-title {
  font-size: 1.8em;
  margin-bottom: 20px;
  font-weight: bold;
}

.cta-text {
  font-size: 1.3em;
  margin-bottom: 15px;
  font-weight: 500;
}

.cta-highlight {
  font-size: 1.5em;
  font-weight: bold;
  font-style: italic;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 1.8em;
  }

  .hero-subtitle {
    font-size: 1em;
  }

  .section-title {
    font-size: 1.5em;
  }

  .cta-title {
    font-size: 1.4em;
  }

  .cta-text {
    font-size: 1.1em;
  }

  .cta-highlight {
    font-size: 1.2em;
  }
}
</style>